// Component for providing copy to clipboard functionality with visual feedback
import { useState } from 'react'
import { Copy, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'

interface ClipboardCopyProps {
  text: string;
  className?: string;
  variant?: 'default' | 'inline' | 'ghost' | 'button';
  labelText?: string;
  children?: React.ReactNode;
}

export function ClipboardCopy({ 
  text, 
  className,
  variant = 'default',
  labelText,
  children
}: ClipboardCopyProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      description: "Copied to clipboard",
      duration: 2000
    });
    setTimeout(() => setCopied(false), 2000);
  };

  // Different styling based on variant
  if (variant === 'inline') {
    return (
      <div 
        className={cn(
          "flex items-center justify-between p-2 rounded-md group",
          "bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
          "hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer",
          className
        )}
        onClick={handleCopy}
      >
        <span className="text-sm text-slate-700 dark:text-slate-100">{children || text}</span>
        <span className="text-slate-500 dark:text-slate-400">
          {copied ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </span>
      </div>
    );
  }

  if (variant === 'ghost') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className={cn("opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800", className)}
      >
        {copied ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
        ) : (
          <Copy className="h-4 w-4 text-slate-600 dark:text-slate-300" />
        )}
      </Button>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className={cn("flex items-center gap-2 bg-white dark:bg-slate-900", className)}
      >
        {copied ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
        ) : (
          <Copy className="h-4 w-4 text-slate-600 dark:text-slate-300" />
        )}
        {labelText || (copied ? 'Copied!' : 'Copy')}
      </Button>
    );
  }

  // Default variant
  return (
    <div 
      className={cn(
        "group cursor-pointer",
        className
      )}
      onClick={handleCopy}
    >
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          className="bg-white/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
        >
          {copied ? (
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
      {children || text}
    </div>
  );
}